<template>
  <q-card flat bordered class="i-camera bg-theme-bg">
    <q-card-section :class="['i-camera-body']">
      <!--未启动时图标-->
      <q-icon v-if="unstart && !computedSnapshot && !unstartImageUrl" :size="unstartIconSize" name="person"
        :color="unstartError ? 'negative' : 'action-configure'" />
      <q-img v-if="unstart && !computedSnapshot && unstartImageUrl" :src="unstartImageUrl" contain :width="imageWidth" :height="imageHeight" />
      <!--camera组件-->
      <i-camera-core v-if="!unstart && !snapshot" height="100%" ref="camera" select-first-device @started="onStateChange(CameraState.Started)"
        @stopped="onStateChange(CameraState.Unstart)" @video-live="onStateChange(CameraState.VideoLive)" @error="onError"
        @notsupported="onNotSupported" @cameras="onEvent('cameras', arguments)" @camera-change="onEvent('camera-change', arguments)">
      </i-camera-core>
      <!--启动后截图-->
      <q-img v-if="computedSnapshot && (unstart || snapshot)" :src="computedSnapshot.dataURL" contain :width="imageWidth" :height="imageHeight" />
    </q-card-section>
    <q-separator />
    <q-card-actions align="around">
      <i-btn :icon="startOrStopRole ? 'play_circle_filled' : 'stop'" :color="startOrStopRole ? 'action-start' : 'action-stop'"
        :disable="startOrStopDisable" :tooltip-text="startOrStopRole ? '开启摄像头':'关闭摄像头'" @click="startOrStop" size="md" dense>
        {{startOrStopRole ?  '开启' : '关闭'}}
      </i-btn>
      <i-btn icon="photo_camera" :disable="captureDisable" tooltip-text="采集照片" @click="capture" size="md" dense>采集</i-btn>
    </q-card-actions>
  </q-card>
</template>
<script>
/*
 * WebCam: https://openbase.com/js/vue-web-cam
 *         https://github.com/VinceG/vue-web-cam
 * 其它资源
 * https://openbase.com/js/vue-cam-vision
 * https://davidwalsh.name/browser-camera
 * https://github.com/addyosmani/getUserMedia.js
 */
// import { WebCam } from 'vue-web-cam'
// eslint-disable-next-line import/no-named-default
import { default as ICameraCore, createSnapshot } from './ICameraCore.vue'

const CameraState = Object.freeze({
  Unstart: 0,
  Starting: 1,
  Started: 2,
  VideoLive: 3,
  Stopping: 4
})
const CameraStateNames = Object.keys(CameraState)

export default {
  components: { ICameraCore },
  data () {
    return {
      CameraState,
      state: CameraState.Unstart,
      notSupported: null,
      startFailed: null,
      defaultSnapshot: null,
      snapshot: null
    }
  },
  props: {
    imageWidth: {
      type: String,
      default: '130px'
    },
    imageHeight: {
      type: String,
      default: '110px'
    },
    unstartIconSize: {
      type: String,
      default: '100px'
    },
    unstartImageId: {
      type: Number,
      default: null
    }
  },
  computed: {
    unstart () {
      return this.state === CameraState.Unstart
    },
    unstartError () {
      return this.notSupported || this.startFailed
    },
    unstartImageUrl () {
      if (this.unstartImageId) {
        return new URL(`/dic/images/${this.unstartImageId}`, this.$$webconfig.bis.api.baseUrl).toString()
      }
      return null
    },
    startOrStopRole () {
      return [CameraState.Unstart, CameraState.Starting].includes(this.state)
    },
    startOrStopColor () {
      return this.startOrStopRole ? 'primary' : 'red'
    },
    startOrStopDisable () {
      return [CameraState.Starting, CameraState.Stopping].includes(this.state)
    },
    captureDisable () {
      return this.state !== CameraState.VideoLive
    },
    computedSnapshot () {
      return this.snapshot ?? this.defaultSnapshot
    },
    // 可能为空
    snapshotBlob () {
      return this.computedSnapshot?.blob
    },
    snapshotDataURL () {
      return this.computedSnapshot?.dataURL
    }
  },
  methods: {
    setState (newState) {
      console.log(`Camera.setState:${CameraStateNames[this.state]} -> ${CameraStateNames[newState]}`)
      this.state = newState
    },
    startOrStop () {
      switch (this.state) {
        case CameraState.Unstart:
          this.notSupported = null
          this.startFailed = null
          this.snapshot = null
          this.setState(CameraState.Starting)
          if (this.$refs.camera) {
            console.log('start camera')
            this.$refs.camera.start()
          }
          break
        case CameraState.Starting:
          this.$q.notify({ type: 'warning', message: '正在启动摄像头, 请稍侯...' })
          break
        case CameraState.Started:
        case CameraState.VideoLive:
          this.setState(CameraState.Stopping)
          if (this.$refs.camera) {
            console.log('stop camera')
            this.$refs.camera.stop()
          }
          break
        case CameraState.Stopping:
          this.$q.notify({ type: 'warning', message: '正在停止摄像头, 请稍侯...' })
          break
        default:
          console.warn('未知CameraState:' + this.state)
          break
      }
    },
    reset () {
      if ([CameraState.Starting, CameraState.Started, CameraState.VideoLive].includes(this.state)) {
        if (this.$refs.camera) {
          console.log('stop camera')
          this.$refs.camera.stop()
        }
      }
      this.defaultSnapshot = null
      this.snapshot = null
    },
    setDefaultSnapshot (imgUrl, imgBlob) {
      this.defaultSnapshot = createSnapshot(imgUrl, imgBlob)
      if (!this.snapshot && this.defaultSnapshot) {
        this.$emit('captured', this.defaultSnapshot)
      }
      return true
    },
    async capture () {
      if (this.state === CameraState.VideoLive) {
        if (this.$refs.camera) {
          const snapshot = await this.$refs.camera.capture()
          if (snapshot) {
            this.$refs.camera.stop()
            this.snapshot = snapshot
            this.$emit('captured', snapshot)
          } else {
            console.warn(`Camera.capture: $refs.camera is ${snapshot}`)
          }
        } else {
          console.warn('Camera.capture: $refs.camera is null')
        }
      } else {
        console.warn(`CameraState:${CameraStateNames[this.state]}, 无法截图`)
      }
    },
    onStateChange (state) {
      console.log(`onStateChange(state: ${CameraStateNames[this.state]})`, state)
      this.setState(state)
    },
    onNotSupported (error) {
      this.$q.notify({ type: 'negative', message: `启动摄像头时发生错误(not supported): ${error?.message}` })
      console.error(`onNotSupported(state: ${CameraStateNames[this.state]})`, error)
      this.notSupported = true
      this.setState(CameraState.Unstart)
    },
    onError (error) {
      this.$q.notify({ type: 'negative', message: `启动摄像头时发生错误: ${error?.message}` })
      console.error(`onError(state: ${CameraStateNames[this.state]})`, error)
      this.startFailed = true
      this.setState(CameraState.Unstart)
    },
    onEvent (name, args) {
      console.log(name, args)
    }
  }
}
</script>

<style lang="scss">
.i-camera {
  display: flex;
  flex-flow: column;

  &-body {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5em !important;
  }
}
</style>
